<template>
  <transition
    name="rr-alert-transition"
    tag="div"
    mode="out-in"
  >
    <div
      class="rr-alert"
      :class="classes"
      v-show="active"
    >
      <div class="rr-alert__container">
        <div class="rr-alert__cols">
          <div class="rr-alert__col rr-alert__col_main">
            <div
              class="rr-alert__icon"
              v-if="iconName"
              :class="{
                'rr-alert__icon_sm' : !title,
                'rr-alert__icon_rotate' : iconName === 'loading'
              }"
            >
              <Icon :name='iconName'></Icon>
            </div>
            <div class="rr-alert__content">
              <div class="rr-alert__title" v-if="title" v-html="title"></div>
              <slot name="text">
                <div class="rr-alert__text" v-if="text" v-html="text"></div>
              </slot>
              <slot></slot>
            </div>
          </div>
          <div
            class="rr-alert__col"
            v-if="!noAction || $slots.buttons"
          >
            <div class="rr-alert__buttons">
              <Link
                v-if="!noAction"
                :text="actionText"
                variant="inherit"
                size="sm"
                tag="button"
                :icon-left="actionIcon"
                @click.stop="onAction"
              ></Link>
              <slot name="buttons"></slot>
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import Icon from '@rr-component-library/icon/src/main';
import Link from '@rr-component-library/link/src/main';

export default {
  name: 'Alert',
  designVersion: '1.0.0',
  components: {
    Icon,
    Link,
  },
  data() {
    return {
      icons: {
        success: ['check-circle-s', 'check-circle'],
        warning: ['warning-s', 'warning'],
        error: ['exclamation-circle-s', 'exclamation-circle'],
      },
      timeout: null,
      active: true,
    };
  },
  props: {
    /**
     * Заголовок
     */
    title: String,
    /**
     * Текст
     */
    text: String,
    /**
     * Иконка
     */
    icon: String,
    /**
     * Состояние
     */
    state: {
      type: String,
      default: 'success',
      validator(value) {
        return ['success', 'warning', 'error'].indexOf(value) !== -1;
      },
    },
    /**
     * Скрывает иконку
     */
    noIcon: {
      type: Boolean,
      default: false,
    },
    /**
     * Текст действия
     */
    actionText: {
      type: String,
      default: 'OK',
    },
    /**
     * Иконка действия
     */
    actionIcon: {
      type: String,
      default: 'check-bold',
    },
    /**
     * Скрывает действие
     */
    noAction: {
      type: Boolean,
      default: false,
    },
    /**
     * Включает автозакрытие алерта
     */
    autoClose: {
      type: Boolean,
      default: false,
    },
    /**
     * Таймаут автозакрытия
     */
    autoCloseTime: {
      type: Number,
      default: 7000,
    },
    /**
     * Включает возможность закрытия алерта
     */
    closable: {
      type: Boolean,
      default: false,
    },
    /**
     * Тип анимации при закрытии/открытии алерта
     */
    transition: {
      type: String,
      default: 'fade',
      validator(value) {
        return ['fade', 'fade-up', 'fade-down'].indexOf(value) !== -1;
      },
    },
    /**
     * Широкий алерт
     */
    wide: {
      type: Boolean,
      default: false,
    },
    /**
     * Расставляет элементы алерта друг под друга на мобильных устройствах
     */
    stacked: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    classes() {
      const classes = {
        'rr-alert_wide': this.wide,
        'rr-alert_fade-up': this.transition === 'fade-up',
        'rr-alert_fade-down': this.transition === 'fade-down',
        'rr-alert_stacked': this.stacked,
      };
      classes[`rr-alert_${this.state}`] = true;
      return classes;
    },
    iconName() {
      if (this.noIcon) {
        return false;
      }
      if (this.icon) {
        return this.icon;
      }
      const iconSize = this.title ? 1 : 0;
      return this.icons[this.state][iconSize];
    },
  },
  methods: {
    onAction(e) {
      e.stopPropagation();
      this.$emit('action', e);
      if (this.closable && !e.defaultPrevented) {
        this.close();
      }
    },
    close() {
      this.active = false;
      clearTimeout(this.timeout);
      this.$emit('close');
    },
  },
  mounted() {
    if (this.closable) {
      this.$el.addEventListener('click', this.close);
      if (this.autoClose) {
        this.timeout = setTimeout(this.close, this.autoCloseTime);
      }
    }
  },
};
</script>

<style lang="scss" scoped>
@import "./Alert";

.rr-alert-transition-enter,
.rr-alert-transition-leave-to {
  opacity: 0;

  &.rr-alert_fade-up {
    transform: translateY(-16px);
  }

  &.rr-alert_fade-down {
    transform: translateY(16px);
  }
}
</style>
